import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { fetchTableData } from '../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';
import { AccidentDateFilter, CustomerNameFilter, CustomerNumberFilter, PlateFilter } from '../../Common/Filters/CommonFilters';
import { customerDetailButtonColumn } from '../../Common/TableColumns';
import { formatDateTR } from '../../../_helper/formatters';
import useDebouncedValue from '../../../_helper/useDebouncedValue';
import { appendQueryParamIfMinLength, normalizeSearchFields } from '../../../_helper/regexHandler';

const Accidents = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [customerNumberSearch, setCustomerNumberSearch] = useState('');
    const [customerNameSearch, setCustomerNameSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [plateSearch, setPlateSearch] = useState('');

    const debouncedCustomerNumberSearch = useDebouncedValue(customerNumberSearch, 500);
    const debouncedCustomerNameSearch = useDebouncedValue(customerNameSearch, 500);
    const debouncedAccidentDateSearch = useDebouncedValue(accidentDateSearch, 500);
    const debouncedPlateSearch = useDebouncedValue(plateSearch, 500);

    const accidentApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accident';

    useEffect(() => {
        let isMounted = true;
        const fetchAccidents = async () => {
            const { normalized, hasInvalid } = normalizeSearchFields([
                { key: 'customerNumber', value: debouncedCustomerNumberSearch, minLength: 3 },
                { key: 'customerName', value: debouncedCustomerNameSearch, minLength: 2 },
                { key: 'plate', value: debouncedPlateSearch, minLength: 2 },
                { key: 'accidentDate', value: debouncedAccidentDateSearch, minLength: 3 }
            ]);

            if (hasInvalid) {
                if (isMounted) setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const queryParams = new URLSearchParams();

            appendQueryParamIfMinLength(queryParams, 'taxOrIdentityNumber', normalized.customerNumber, 3);
            appendQueryParamIfMinLength(queryParams, 'customerNameOrCompanyName', normalized.customerName, 2);
            appendQueryParamIfMinLength(queryParams, 'licensePlate', normalized.plate, 2);
            appendQueryParamIfMinLength(queryParams, 'accidentDate', normalized.accidentDate, 3);

            const apiUrl = queryParams.toString()
                ? `${accidentApiUrl}?${queryParams.toString()}`
                : accidentApiUrl;

            await fetchTableData({
                apiEndpoint: apiUrl,
                page,
                pageSize,
                search: null,
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            });

            if (isMounted) setIsLoading(false);
        };

        fetchAccidents();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, debouncedCustomerNumberSearch, debouncedCustomerNameSearch, debouncedAccidentDateSearch, debouncedPlateSearch]);

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const columns = [
        customerDetailButtonColumn({
            getCustomerId: (row) => row.licensePlate?.customer?.[0],
            onClick: handleCustomerDetail
        }),
        { name: 'Kaza Numarası', selector: row => row.accidentNumber || '', sortable: true },
        { name: 'Kaza Tarihi', selector: row => formatDateTR(row.accidentDate), sortable: true },
        { 
            name: 'Atanan Uzmanlar', 
            selector: row => {
                if (!row.appointedExperts?.length) return '';
                return row.appointedExperts
                    .map(expertObj => expertObj.expert?.join(', '))
                    .filter(Boolean)
                    .join(', ') || '';
            },
            sortable: true 
        },
        { name: 'Karşı Taraf Plaka Numarası', selector: row => row.counterpartyLicensePlateNumber || '', sortable: true },
        { name: 'Plaka', selector: row => row.licensePlate?.licensePlateNumber || '', sortable: true },
    ];

    return (
        <>
            <Breadcrumbs parent='Müşteri Alanları' title='Kazalar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Kazalar</H5>
                                <span>Tanımlanmış olan kazalar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <CustomerNumberFilter
                                        value={customerNumberSearch}
                                        onChange={(e) => { setCustomerNumberSearch(e.target.value); setPage(1); }}
                                    />
                                    <CustomerNameFilter
                                        value={customerNameSearch}
                                        onChange={(e) => { setCustomerNameSearch(e.target.value); setPage(1); }}
                                    />
                                    <PlateFilter
                                        value={plateSearch}
                                        onChange={(e) => { setPlateSearch(e.target.value); setPage(1); }}
                                    />
                                    <AccidentDateFilter
                                        value={accidentDateSearch}
                                        onChange={(e) => { setAccidentDateSearch(e.target.value); setPage(1); }}
                                    />
                                </Row>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationServer
                                        stickyActionPosition="left"
                                        selectableRows={false}
                                        paginationTotalRows={totalRows}
                                        paginationPerPage={pageSize}
                                        paginationDefaultPage={page}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : "Kaza bulunamadı"}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Accidents;
